import { useState, useMemo, useEffect } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBox,
  faBoxes,
  faCalendarPlus,
  faChartLine,
  faMoneyBillWave,
  faShoppingCart,
  faPercent,
  faTag,
  faArrowTrendDown,
  faCalculator,
  faChevronLeft,
  faChevronRight,
  faArrowTrendUp,
  faCalendarDay,
  faEdit,
  faInfoCircle,
  faClipboardList,
  faLayerGroup,
  faArrowRight,
  faArrowLeft,
  faCalendarAlt,
  faSpinner,
  faClock,
  faWarehouse,
  faChevronDown,
  faChevronUp,
  faStore,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { stockService } from "../../../services/service/stockService";
import { useAuth } from "../../../hooks";

const formatCurrency = (n) => `${Number(n).toLocaleString()} TZS`;

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions,
}) => {
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200">
      <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
        <span>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}{" "}
          to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
          records
        </span>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-blue-800/30 focus:border-blue-800 bg-white"
        >
          {itemsPerPageOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        </Button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((number) => (
            <Button
              key={number}
              variant={currentPage === number ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(number)}
              className={`h-9 w-9 p-0 ${
                currentPage === number
                  ? "bg-blue-800 hover:bg-blue-900 text-white"
                  : "border-gray-300 hover:border-blue-800 hover:bg-blue-50"
              }`}
            >
              {number}
            </Button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-9 px-3 border-gray-300 hover:border-blue-800"
        >
          <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

const StockDetails = ({ stock, shop, onClose, onEdit }) => {
  const { hasPermission } = useAuth();
  const canManageStock = hasPermission(["manage_stock", "manage_shop_modules"]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [activeTab, setActiveTab] = useState("product");
  const [stockDetails, setStockDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStockLevelOpen, setIsStockLevelOpen] = useState(false);
  const [isStockAgeOpen, setIsStockAgeOpen] = useState(false);
  const [isMovementHistoryOpen, setIsMovementHistoryOpen] = useState(true);

  const itemsPerPageOptions = [5, 10, 15, 20, 30, 50, 100, 250];
  const stockId = stock?.stockId || stock?.id;

  useEffect(() => {
    setStockDetails(null);
    if (stockId) {
      fetchStockDetails(stockId);
    }
  }, [stockId]);

  const fetchStockDetails = async (id) => {
    setIsLoading(true);
    try {
      const response = await stockService.getWithSales(id);
      const payload = response?.data?.data ?? response?.data ?? null;
      setStockDetails(payload);
    } catch (error) {
      console.error("Error fetching stock details:", error);
      setStockDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateShort = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getAgeCategory = (days) => {
    if (days < 7) return "Fresh (< 1 week)";
    if (days < 14) return "1-2 weeks";
    if (days < 30) return "2-4 weeks";
    if (days < 60) return "1-2 months";
    return "Old (> 2 months)";
  };

  const getAgeBadgeColor = (days) => {
    if (days < 7) return "bg-green-100 text-green-700 border-green-200";
    if (days < 14) return "bg-yellow-100 text-yellow-700 border-yellow-200";
    if (days < 30) return "bg-orange-100 text-orange-700 border-orange-200";
    if (days < 60) return "bg-red-100 text-red-700 border-red-200";
    return "bg-purple-100 text-purple-700 border-purple-200";
  };

  const mergedProduct = useMemo(() => {
    if (!stock) return null;

    const details = stockDetails?.stock || {};
    const summary = stockDetails?.summary || stock.summary;
    const movements = Array.isArray(stockDetails?.movements)
      ? stockDetails.movements
      : Array.isArray(stock.movements)
        ? stock.movements
        : [];
    const sales = Array.isArray(stockDetails?.sales)
      ? stockDetails.sales
      : Array.isArray(stock.sales)
        ? stock.sales
        : [];

    return {
      ...stock,
      ...details,
      summary,
      movements,
      sales,
      product: {
        ...stock.product,
        product_name: stock.product?.product_name || details.productName,
        package_type: stock.product?.package_type || stock.packageType,
        no_of_items: stock.product?.no_of_items || stock.noOfItems,
      },
    };
  }, [stock, stockDetails]);

  const metrics = useMemo(() => {
    if (!mergedProduct) {
      return {
        dozenAmount: 0,
        itemsPerPackage: 0,
        totalItems: 0,
        costPerItem: 0,
        totalPurchase: 0,
        totalSelling: 0,
        expectedProfit: 0,
        profitPerItem: 0,
        profitMargin: 0,
        isProfit: false,
        isLoss: false,
      };
    }

    const dozenAmount = Number(
      mergedProduct.dozen_amount || mergedProduct.dozenAmount || 0,
    );
    const itemsPerPackage = Number(
      mergedProduct.items_per_package ||
        mergedProduct.product?.no_of_items ||
        mergedProduct.noOfItems ||
        0,
    );

    const totalItems = Number(
      mergedProduct.total_items ??
        mergedProduct.summary?.totalItems ??
        dozenAmount * itemsPerPackage,
    );

    const totalPurchase = Number(
      mergedProduct.purchase_price_total ||
        mergedProduct.purchasePriceTotal ||
        0,
    );
    const totalSelling = Number(
      mergedProduct.selling_price_total || mergedProduct.sellingPriceTotal || 0,
    );

    const costPerItem = Number(
      mergedProduct.cost_per_item ??
        (totalItems > 0 ? totalPurchase / totalItems : 0),
    );

    const expectedProfit = Number(
      mergedProduct.expected_profit ?? totalSelling - totalPurchase,
    );

    const profitPerItem = Number(
      mergedProduct.expected_profit_per_item ??
        (totalItems > 0 ? expectedProfit / totalItems : 0),
    );

    const profitMargin = Number(
      mergedProduct.expected_profit_margin ??
        (totalPurchase > 0 ? (expectedProfit / totalPurchase) * 100 : 0),
    );

    return {
      dozenAmount,
      itemsPerPackage,
      totalItems,
      costPerItem,
      totalPurchase,
      totalSelling,
      expectedProfit,
      profitPerItem,
      profitMargin,
      isProfit: expectedProfit >= 0,
      isLoss: expectedProfit < 0,
    };
  }, [mergedProduct]);

  const capitalize = (s) =>
    s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "";

  const packageType =
    mergedProduct?.product?.package_type ||
    mergedProduct?.package_type ||
    "Package";

  const stockMovements = useMemo(() => {
    if (!mergedProduct) return [];

    const backendMovements = Array.isArray(mergedProduct.movements)
      ? mergedProduct.movements
      : [];

    if (!backendMovements.length) return [];

    return backendMovements.map((m, idx) => {
      const daysInStock = Number(m.daysInStock ?? 0);

      let age;
      if (m.type === "IN") {
        age = "Just received";
      } else if (m.type === "BALANCE") {
        age = `${daysInStock} days in stock (${getAgeCategory(daysInStock)})`;
      } else {
        age = `${daysInStock} days before sale (${getAgeCategory(daysInStock)})`;
      }

      return {
        ...m,
        id: m.id ?? idx + 1,
        status:
          m.status ??
          (m.type === "BALANCE"
            ? Number(m.remaining ?? 0) > 0
              ? "Available"
              : "Sold Out"
            : "Completed"),
        age,
      };
    });
  }, [mergedProduct]);

  const totalIn = Number(mergedProduct?.summary?.totalIn ?? 0);
  const totalOut = Number(mergedProduct?.summary?.totalOut ?? 0);
  const totalBalance = Number(mergedProduct?.summary?.totalRemaining ?? 0);

  const totalMovementItems = stockMovements.length;
  const totalPages = Math.ceil(totalMovementItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentMovements = stockMovements.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const getCurrentStockAge = () => {
    const balance = stockMovements.find((m) => m.type === "BALANCE");
    return balance?.daysInStock || 0;
  };

  const getAverageDaysBeforeSale = () => {
    const outMovements = stockMovements.filter((m) => m.type === "OUT");
    if (outMovements.length === 0) return 0;
    const total = outMovements.reduce(
      (sum, m) => sum + (m.daysInStock || 0),
      0,
    );
    return Math.round(total / outMovements.length);
  };

  const getDaysSinceLastSale = () => {
    const outMovements = stockMovements.filter((m) => m.type === "OUT");
    if (outMovements.length === 0) return 0;
    const lastSale = outMovements[outMovements.length - 1];
    const today = new Date();
    const lastSaleDate = new Date(lastSale.date);
    return Math.floor((today - lastSaleDate) / (1000 * 60 * 60 * 24));
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const getProductType = () => {
    if (mergedProduct?.productType) return mergedProduct.productType;
    if (mergedProduct?.product?.product_type)
      return mergedProduct.product.product_type;
    if (mergedProduct?.product?.product_category?.product_type?.name) {
      return mergedProduct.product.product_category.product_type.name;
    }
    return "N/A";
  };

  const getCategoryName = () => {
    if (mergedProduct?.productCategory) return mergedProduct.productCategory;
    if (mergedProduct?.product?.category_name)
      return mergedProduct.product.category_name;
    if (mergedProduct?.product?.product_category?.category_name) {
      return mergedProduct.product.product_category.category_name;
    }
    return "N/A";
  };

  const InfoItem = ({ icon, label, value, highlight = false }) => (
    <div
      className={`flex items-start gap-3 p-3 rounded-md border transition-all duration-200 ${
        highlight
          ? "bg-blue-50 border-blue-200 shadow-sm"
          : "bg-white border-gray-200 hover:border-blue-200"
      }`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          highlight ? "bg-blue-100" : "bg-gray-100"
        }`}
      >
        <FontAwesomeIcon
          icon={icon}
          className={`h-5 w-5 ${highlight ? "text-blue-600" : "text-gray-600"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">
          {label}
        </p>
        <p
          className={`text-sm font-medium truncate ${highlight ? "text-blue-700" : "text-gray-900"}`}
        >
          {value || "-"}
        </p>
      </div>
    </div>
  );

  const StatCard = ({ icon, label, value, color, borderColor, valueColor }) => (
    <div
      className={`bg-white rounded-lg p-4 text-center border-2 ${borderColor} shadow-sm`}
    >
      <div
        className={`w-10 h-10 rounded-full ${color} flex items-center justify-center mx-auto mb-2`}
      >
        <FontAwesomeIcon icon={icon} className="h-5 w-5 text-white" />
      </div>
      <p className="text-sm uppercase tracking-wider text-gray-600 font-semibold">
        {label}
      </p>
      <p className={`text-md font-bold ${valueColor}`}>{value}</p>
    </div>
  );

  const tabs = [
    { id: "product", label: "Product details", icon: faInfoCircle },
    { id: "stock", label: "Stock details", icon: faClipboardList },
    { id: "financial", label: "Financial details", icon: faChartLine },
  ];

  if (!mergedProduct) return null;

  const currentStockAge = getCurrentStockAge();
  const avgDaysBeforeSale = getAverageDaysBeforeSale();
  const daysSinceLastSale = getDaysSinceLastSale();

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Stock details"
      maxWidth="sm:max-w-6xl"
      footer={
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Close
          </Button>
          {canManageStock && (
            <Button
              onClick={() => {
                onClose();
                if (onEdit) onEdit(stock);
              }}
              className="gap-2 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white px-6 shadow-md hover:shadow-lg transition-all duration-200"
            >
              <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
              Edit Stock
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <FontAwesomeIcon
                icon={faSpinner}
                className="h-8 w-8 text-blue-600 animate-spin"
              />
              <p className="text-sm text-gray-600">
                Loading product details...
              </p>
            </div>
          </div>
        )}

        {!isLoading && (
          <>
            <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-white rounded-md border border-blue-100">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shadow-sm">
                <FontAwesomeIcon
                  icon={faBox}
                  className="text-blue-600 h-5 w-5"
                />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">
                  Stock information
                </p>
                <p className="text-sm text-gray-600">
                  Complete stock details and financial summary
                </p>
              </div>
              <Badge
                variant="outline"
                className="ml-auto bg-green-50 text-green-700 border-green-200"
              >
                <FontAwesomeIcon
                  icon={faArrowTrendUp}
                  className="mr-1.5 h-3 w-3"
                />
                {mergedProduct.status || "ACTIVE"}
              </Badge>
            </div>

            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex-1 sm:flex-none justify-center ${
                    activeTab === tab.id
                      ? "bg-white text-blue-700 shadow-sm ring-1 ring-blue-200"
                      : "text-gray-600 hover:text-gray-700 hover:bg-gray-200/50"
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>

            <div className="mt-1">
              {activeTab === "product" && (
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-3">
                      <h4 className="text-md font-semibold text-gray-700 flex items-center gap-2 pb-2 border-b border-gray-200">
                        <FontAwesomeIcon
                          icon={faStore}
                          className="text-blue-600 h-4 w-4"
                        />
                        Shop Information
                      </h4>
                      <InfoItem
                        icon={faStore}
                        label="Shop Name"
                        value={
                          shop?.shop_name || mergedProduct.shopName || "N/A"
                        }
                        highlight={true}
                      />
                      <InfoItem
                        icon={faLocationDot}
                        label="Location"
                        value={shop?.location || "N/A"}
                      />
                      <InfoItem
                        icon={faTag}
                        label="Shop Type"
                        value={shop?.shop_type || "N/A"}
                      />
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-md font-semibold text-gray-700 flex items-center gap-2 pb-2 border-b border-gray-200">
                        <FontAwesomeIcon
                          icon={faTag}
                          className="text-blue-600 h-4 w-4"
                        />
                        Product Information
                      </h4>
                      <InfoItem
                        icon={faTag}
                        label="Product Name"
                        value={
                          mergedProduct.product?.product_name ||
                          mergedProduct.productName ||
                          "N/A"
                        }
                        highlight={true}
                      />
                      <InfoItem
                        icon={faLayerGroup}
                        label="Type"
                        value={getProductType()}
                      />
                      <InfoItem
                        icon={faLayerGroup}
                        label="Category"
                        value={getCategoryName()}
                      />
                      <InfoItem
                        icon={faBoxes}
                        label="Package Type"
                        value={
                          mergedProduct.product?.package_type ||
                          mergedProduct.packageType ||
                          "N/A"
                        }
                      />
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-md font-semibold text-gray-700 flex items-center gap-2 pb-2 border-b border-gray-200">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="text-blue-600 h-4 w-4"
                        />
                        Purchase Information
                      </h4>
                      <InfoItem
                        icon={faCalendarPlus}
                        label="Purchase Date"
                        value={formatDate(
                          mergedProduct.purchase_date ||
                            mergedProduct.purchaseDate,
                        )}
                        highlight={true}
                      />
                      <InfoItem
                        icon={faCalendarDay}
                        label="Created At"
                        value={formatDate(
                          mergedProduct.created_at || mergedProduct.createdAt,
                        )}
                      />
                      <InfoItem
                        icon={faCalendarDay}
                        label="Updated At"
                        value={formatDate(
                          mergedProduct.updated_at || mergedProduct.updatedAt,
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "stock" && (
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard
                      icon={faBox}
                      label="Package Type"
                      value={
                        mergedProduct.product?.package_type ||
                        mergedProduct.packageType ||
                        "N/A"
                      }
                      color="bg-orange-600"
                      borderColor="border-orange-200"
                      valueColor="text-gray-900"
                    />
                    <StatCard
                      icon={faBoxes}
                      label={`${capitalize(packageType)} Amount`}
                      value={
                        mergedProduct.dozen_amount ||
                        mergedProduct.dozenAmount ||
                        0
                      }
                      color="bg-green-600"
                      borderColor="border-green-200"
                      valueColor="text-gray-900"
                    />
                    <StatCard
                      icon={faCalculator}
                      label="Items per Package"
                      value={
                        mergedProduct.items_per_package ||
                        mergedProduct.product?.no_of_items ||
                        mergedProduct.noOfItems ||
                        0
                      }
                      color="bg-purple-600"
                      borderColor="border-purple-200"
                      valueColor="text-gray-900"
                    />
                    <StatCard
                      icon={faShoppingCart}
                      label="Total Items"
                      value={metrics.totalItems}
                      color="bg-blue-600"
                      borderColor="border-blue-200"
                      valueColor="text-gray-900"
                    />
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <button
                      onClick={() => setIsStockLevelOpen(!isStockLevelOpen)}
                      className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faWarehouse}
                          className="text-blue-600 h-4 w-4"
                        />
                        <h4 className="text-md font-semibold text-gray-700">
                          Stock Level Overview
                        </h4>
                        <Badge
                          variant="outline"
                          className="ml-2 text-xs bg-gray-100 border-gray-200"
                        >
                          {isStockLevelOpen ? "Hide" : "Show"} details
                        </Badge>
                      </div>
                      <FontAwesomeIcon
                        icon={isStockLevelOpen ? faChevronUp : faChevronDown}
                        className="text-gray-500 h-4 w-4 transition-transform duration-200"
                      />
                    </button>
                    {isStockLevelOpen && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="space-y-2">
                          <InfoItem
                            icon={faArrowRight}
                            label="Total Stock In"
                            value={`${totalIn} items`}
                            highlight={true}
                          />
                          <InfoItem
                            icon={faArrowLeft}
                            label="Total Stock Out"
                            value={`${totalOut} items`}
                          />
                          <InfoItem
                            icon={faBox}
                            label="Remaining Balance"
                            value={`${totalBalance} items`}
                            highlight={totalBalance > 0}
                          />
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600 font-medium">
                                Stock Level
                              </span>
                              <span className="font-medium text-gray-900">
                                {metrics.totalItems > 0
                                  ? Math.round(
                                      (totalBalance / metrics.totalItems) * 100,
                                    )
                                  : 0}
                                %
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-lg h-2 overflow-hidden">
                              <div
                                className={`h-2 rounded-lg transition-all duration-500 ${
                                  totalBalance / metrics.totalItems <= 0.1
                                    ? "bg-red-500"
                                    : totalBalance / metrics.totalItems <= 0.25
                                      ? "bg-orange-500"
                                      : totalBalance / metrics.totalItems <= 0.5
                                        ? "bg-yellow-500"
                                        : totalBalance / metrics.totalItems <=
                                            0.75
                                          ? "bg-emerald-500"
                                          : "bg-green-500"
                                }`}
                                style={{
                                  width: `${Math.min((totalBalance / metrics.totalItems) * 100, 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <button
                      onClick={() => setIsStockAgeOpen(!isStockAgeOpen)}
                      className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faClock}
                          className="text-amber-600 h-4 w-4"
                        />
                        <h4 className="text-md font-semibold text-gray-700">
                          Stock Age Analysis
                        </h4>
                        <Badge
                          variant="outline"
                          className="ml-2 text-xs bg-gray-100 border-gray-200"
                        >
                          {isStockAgeOpen ? "Hide" : "Show"} details
                        </Badge>
                      </div>
                      <FontAwesomeIcon
                        icon={isStockAgeOpen ? faChevronUp : faChevronDown}
                        className="text-gray-500 h-4 w-4 transition-transform duration-200"
                      />
                    </button>
                    {isStockAgeOpen && (
                      <div className="p-4 border-t border-gray-200">
                        <div className="space-y-2">
                          <InfoItem
                            icon={faClock}
                            label="Current Stock Age"
                            value={`${currentStockAge} days in stock`}
                            highlight={true}
                          />
                          <InfoItem
                            icon={faChartLine}
                            label="Average Days Before Sale"
                            value={`${avgDaysBeforeSale} days`}
                          />
                          <InfoItem
                            icon={faCalendarAlt}
                            label="Days Since Last Sale"
                            value={`${daysSinceLastSale} days`}
                          />
                          <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between text-sm">
                            <span className="text-gray-600 font-medium">
                              📦 {metrics.dozenAmount}{" "}
                              {mergedProduct.product?.package_type ||
                                mergedProduct.packageType ||
                                "packages"}
                            </span>
                            <span className="text-gray-600 font-medium">
                              📊 {metrics.itemsPerPackage} items per package
                            </span>
                            <span className="text-gray-600 font-medium">
                              🔄{" "}
                              {totalBalance > 0 ? "Active stock" : "Sold out"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                    <button
                      onClick={() =>
                        setIsMovementHistoryOpen(!isMovementHistoryOpen)
                      }
                      className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon
                          icon={faClipboardList}
                          className="text-blue-600 h-4 w-4"
                        />
                        <h4 className="text-md font-semibold text-gray-700">
                          Stock Movement History
                        </h4>
                        <Badge
                          variant="outline"
                          className="ml-2 text-xs bg-gray-100 border-gray-200"
                        >
                          {stockMovements.length} events
                        </Badge>
                        <Badge
                          variant="outline"
                          className="ml-1 text-xs bg-gray-100 border-gray-200"
                        >
                          {isMovementHistoryOpen ? "Hide" : "Show"} details
                        </Badge>
                      </div>
                      <FontAwesomeIcon
                        icon={
                          isMovementHistoryOpen ? faChevronUp : faChevronDown
                        }
                        className="text-gray-500 h-4 w-4 transition-transform duration-200"
                      />
                    </button>
                    {isMovementHistoryOpen && (
                      <div className="border-t border-gray-200">
                        <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-green-500"></span>{" "}
                              IN
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>{" "}
                              OUT
                            </span>
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>{" "}
                              BALANCE
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            Scroll horizontally for more details
                          </span>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap">
                                  #
                                </th>
                                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap">
                                  Type
                                </th>
                                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap">
                                  Date
                                </th>
                                <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap">
                                  Qty
                                </th>
                                <th className="text-right text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap">
                                  Remaining
                                </th>
                                <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap">
                                  Days in Stock
                                </th>
                                <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 whitespace-nowrap hidden md:table-cell">
                                  Age
                                </th>
                                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2.5 px-3 min-w-[200px]">
                                  Description
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {currentMovements.length > 0 ? (
                                currentMovements.map((movement, i) => {
                                  const globalIndex = startIndex + i + 1;
                                  const isIn = movement.type === "IN";
                                  const isOut = movement.type === "OUT";
                                  const isBalance = movement.type === "BALANCE";
                                  const daysInStock = movement.daysInStock || 0;
                                  const ageColor =
                                    getAgeBadgeColor(daysInStock);

                                  return (
                                    <tr
                                      key={movement.id}
                                      className="hover:bg-blue-50/30 transition-colors duration-150"
                                    >
                                      <td className="py-2.5 px-3 text-xs text-gray-600 font-medium whitespace-nowrap">
                                        {globalIndex}
                                      </td>
                                      <td className="py-2.5 px-3 whitespace-nowrap">
                                        <Badge
                                          variant="outline"
                                          className={`border font-medium px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 w-fit ${
                                            isIn
                                              ? "bg-green-100 text-green-700 border-green-200"
                                              : isOut
                                                ? "bg-red-100 text-red-700 border-red-200"
                                                : "bg-blue-100 text-blue-700 border-blue-200"
                                          }`}
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              isIn
                                                ? faArrowRight
                                                : isOut
                                                  ? faArrowLeft
                                                  : faBox
                                            }
                                            className="h-2.5 w-2.5"
                                          />
                                          {isIn
                                            ? "IN"
                                            : isOut
                                              ? "OUT"
                                              : "BALANCE"}
                                        </Badge>
                                      </td>
                                      <td className="py-2.5 px-3 text-xs text-gray-600 font-medium whitespace-nowrap">
                                        {formatDateShort(movement.date)}
                                      </td>
                                      <td className="py-2.5 px-3 text-right font-semibold text-gray-900 text-sm whitespace-nowrap">
                                        {movement.quantity}
                                      </td>
                                      <td className="py-2.5 px-3 text-right font-semibold text-blue-600 text-sm whitespace-nowrap">
                                        {movement.remaining}
                                      </td>
                                      <td className="py-2.5 px-3 text-center whitespace-nowrap">
                                        <Badge
                                          variant="outline"
                                          className={`border px-2.5 py-1 rounded-full text-xs font-medium ${ageColor}`}
                                        >
                                          <FontAwesomeIcon
                                            icon={faClock}
                                            className="h-2.5 w-2.5 mr-1"
                                          />
                                          {daysInStock}{" "}
                                          {daysInStock === 1 ? "day" : "days"}
                                        </Badge>
                                      </td>
                                      <td className="py-2.5 px-3 text-center hidden md:table-cell whitespace-nowrap">
                                        <span
                                          className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                            isBalance && daysInStock > 60
                                              ? "bg-red-100 text-red-700"
                                              : isBalance && daysInStock > 30
                                                ? "bg-orange-100 text-orange-700"
                                                : isBalance && daysInStock > 14
                                                  ? "bg-yellow-100 text-yellow-700"
                                                  : isBalance
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-gray-100 text-gray-600"
                                          }`}
                                        >
                                          {movement.age}
                                        </span>
                                      </td>
                                      <td className="py-2.5 px-3 text-xs text-gray-600 min-w-[200px] max-w-xs">
                                        <div className="text-gray-600 font-medium break-words">
                                          {movement.description}
                                          {movement.sellerName && (
                                            <span className="block text-xs text-gray-500">
                                              👤 {movement.sellerName}
                                            </span>
                                          )}
                                          {isBalance && daysInStock > 30 && (
                                            <span className="block text-xs text-orange-500 font-medium">
                                              ⚠️ Old stock - consider promotion
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan={8} className="text-center py-8">
                                    <div className="flex flex-col items-center gap-2 text-gray-400">
                                      <FontAwesomeIcon
                                        icon={faBox}
                                        className="h-8 w-8"
                                      />
                                      <p className="text-sm">
                                        No stock movements found
                                      </p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {totalMovementItems > 0 && (
                          <div className="p-3 border-t border-gray-200">
                            <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              itemsPerPage={itemsPerPage}
                              totalItems={totalMovementItems}
                              onPageChange={setCurrentPage}
                              onItemsPerPageChange={handleItemsPerPageChange}
                              itemsPerPageOptions={itemsPerPageOptions}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-200 p-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 font-bold">
                        📊 Summary
                      </span>
                      <span className="text-gray-700 font-medium">
                        <span className="font-semibold text-green-600">
                          {totalIn}
                        </span>{" "}
                        received,
                        <span className="font-semibold text-red-600 ml-1">
                          {totalOut}
                        </span>{" "}
                        sold,
                        <span className="font-semibold text-blue-600 ml-1">
                          {totalBalance}
                        </span>{" "}
                        remaining
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-gray-600 font-semibold">
                      <span>
                        📦 {metrics.dozenAmount}{" "}
                        {mergedProduct.product?.package_type ||
                          mergedProduct.packageType ||
                          "packages"}
                      </span>
                      <span>📋 {metrics.itemsPerPackage} items/pkg</span>
                      <span>
                        📈{" "}
                        {totalBalance > 0
                          ? `${Math.round((totalBalance / metrics.totalItems) * 100)}% available`
                          : "Sold out"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "financial" && (
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatCard
                      icon={faMoneyBillWave}
                      label="Purchase Price"
                      value={formatCurrency(metrics.totalPurchase)}
                      color="bg-blue-600"
                      borderColor="border-blue-200"
                      valueColor="text-gray-900"
                    />
                    <StatCard
                      icon={faChartLine}
                      label="Selling Price"
                      value={formatCurrency(metrics.totalSelling)}
                      color="bg-green-600"
                      borderColor="border-green-200"
                      valueColor="text-green-600"
                    />
                    <StatCard
                      icon={
                        metrics.expectedProfit > 0
                          ? faArrowTrendUp
                          : faArrowTrendDown
                      }
                      label={
                        metrics.expectedProfit > 0
                          ? "Expected Profit"
                          : metrics.expectedProfit < 0
                            ? "Expected Loss"
                            : "Break Even"
                      }
                      value={
                        metrics.expectedProfit > 0
                          ? formatCurrency(metrics.expectedProfit)
                          : metrics.expectedProfit < 0
                            ? `(${formatCurrency(Math.abs(metrics.expectedProfit))})`
                            : formatCurrency(0)
                      }
                      color={
                        metrics.expectedProfit > 0
                          ? "bg-green-600"
                          : metrics.expectedProfit < 0
                            ? "bg-red-600"
                            : "bg-gray-600"
                      }
                      borderColor={
                        metrics.expectedProfit > 0
                          ? "border-green-200"
                          : metrics.expectedProfit < 0
                            ? "border-red-200"
                            : "border-gray-200"
                      }
                      valueColor={
                        metrics.expectedProfit > 0
                          ? "text-green-600"
                          : metrics.expectedProfit < 0
                            ? "text-red-600"
                            : "text-gray-600"
                      }
                    />
                    <StatCard
                      icon={faCalculator}
                      label="Profit Per Item"
                      value={
                        metrics.profitPerItem > 0
                          ? formatCurrency(metrics.profitPerItem)
                          : metrics.profitPerItem < 0
                            ? `(${formatCurrency(Math.abs(metrics.profitPerItem))})`
                            : formatCurrency(0)
                      }
                      color={
                        metrics.profitPerItem > 0
                          ? "bg-purple-600"
                          : "bg-red-600"
                      }
                      borderColor={
                        metrics.profitPerItem > 0
                          ? "border-purple-200"
                          : "border-red-200"
                      }
                      valueColor={
                        metrics.profitPerItem > 0
                          ? "text-purple-600"
                          : "text-red-600"
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <h4 className="text-md font-semibold text-blue-700 flex items-center gap-2 pb-2 border-b border-blue-200">
                        <FontAwesomeIcon
                          icon={faMoneyBillWave}
                          className="text-blue-600 h-4 w-4"
                        />
                        Purchase Details
                      </h4>
                      <div className="space-y-2">
                        <InfoItem
                          icon={faMoneyBillWave}
                          label="Purchase Price (Total)"
                          value={formatCurrency(metrics.totalPurchase)}
                          highlight={true}
                        />
                        <InfoItem
                          icon={faCalculator}
                          label="Cost Per Item"
                          value={formatCurrency(metrics.costPerItem)}
                        />
                        <InfoItem
                          icon={faBoxes}
                          label="Total Items Purchased"
                          value={metrics.totalItems}
                        />
                        <InfoItem
                          icon={faBox}
                          label="Package Quantity"
                          value={`${metrics.dozenAmount} × ${metrics.itemsPerPackage} items`}
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-md font-semibold text-green-700 flex items-center gap-2 pb-2 border-b border-green-200">
                        <FontAwesomeIcon
                          icon={faChartLine}
                          className="text-green-600 h-4 w-4"
                        />
                        Selling Details
                      </h4>
                      <div className="space-y-2">
                        <InfoItem
                          icon={faCalculator}
                          label="Price Per Item"
                          value={formatCurrency(
                            mergedProduct.selling_price_per_item ??
                              mergedProduct.selling_price ??
                              mergedProduct.sellingPrice ??
                              0,
                          )}
                        />
                        <InfoItem
                          icon={faChartLine}
                          label="Selling Price (Total)"
                          value={formatCurrency(metrics.totalSelling)}
                          highlight={true}
                        />
                        <InfoItem
                          icon={faMoneyBillWave}
                          label="Total Expected Profit"
                          value={formatCurrency(metrics.expectedProfit)}
                        />
                        <InfoItem
                          icon={faPercent}
                          label="Expected Profit Margin"
                          value={
                            metrics.expectedProfit > 0
                              ? `${((metrics.expectedProfit / metrics.totalPurchase) * 100).toFixed(1)}%`
                              : metrics.expectedProfit < 0
                                ? `-${((Math.abs(metrics.expectedProfit) / metrics.totalPurchase) * 100).toFixed(1)}%`
                                : "0%"
                          }
                          highlight={metrics.expectedProfit > 0}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default StockDetails;
